import {
  WEAPON_POWERUPS,
  type PowerupType,
  type RosterEntry,
  type SnapshotDTO,
  type TankDTO,
} from "../protocol/index.js";
import {
  BF_EXPLOSIVE,
  BF_HOMING,
  BF_PIERCE,
  FLAG_STATES,
  MSG_SNAPSHOT_SLIM,
  PUP_CODES,
  decAngle,
  decDs,
} from "./codes.js";

/** Rebuild a full SnapshotDTO by joining the packed dynamic fields with the
 *  static roster info. Tanks with no roster entry are skipped. */
export function decodeSnapshot(buf: ArrayBuffer, roster: Map<number, RosterEntry>, previous?: SnapshotDTO | null): SnapshotDTO {
  const dv = new DataView(buf);
  const tag = dv.getUint8(0);
  if (tag === MSG_SNAPSHOT_SLIM) return decodeSlimSnapshot(dv, roster, previous ?? null);
  let o = 1; // skip tag

  const tanks: TankDTO[] = [];
  const tankCount = dv.getUint8(o++);
  for (let i = 0; i < tankCount; i++) {
    const index = dv.getUint8(o++);
    const x = dv.getUint16(o, true);
    o += 2;
    const y = dv.getUint16(o, true);
    o += 2;
    const bodyAngle = decAngle(dv.getInt16(o, true));
    o += 2;
    const turretAngle = decAngle(dv.getInt16(o, true));
    o += 2;
    const flags = dv.getUint8(o++);
    const hp = dv.getUint8(o++);
    const ammo = dv.getUint8(o++);
    const score = dv.getUint16(o, true);
    o += 2;
    const respawnIn = decDs(dv.getUint8(o++));
    const reloadIn = decDs(dv.getUint8(o++));
    const weaponCharges: Partial<Record<PowerupType, number>> = {};
    for (const w of WEAPON_POWERUPS) {
      const c = dv.getUint8(o++);
      if (c > 0) weaponCharges[w] = c;
    }
    const livesLeft = dv.getUint8(o++);
    const captures = dv.getUint8(o++);
    const r = roster.get(index);
    tanks.push({
      index,
      id: r?.id ?? String(index),
      name: r?.name ?? "?",
      color: r?.color ?? "#888888",
      team: r?.team ?? -1,
      maxHp: r?.maxHp ?? hp,
      maxAmmo: r?.maxAmmo ?? ammo,
      x,
      y,
      bodyAngle,
      turretAngle,
      alive: (flags & 1) !== 0,
      boosted: (flags & 2) !== 0,
      shielded: (flags & 4) !== 0,
      charging: (flags & 8) !== 0,
      scoped: (flags & 16) !== 0,
      hp,
      ammo,
      score,
      respawnIn,
      reloadIn,
      weaponCharges,
      livesLeft,
      captures,
    });
  }

  const bullets = [];
  const bulletCount = dv.getUint8(o++);
  for (let i = 0; i < bulletCount; i++) {
    const id = dv.getUint16(o, true);
    o += 2;
    const x = dv.getUint16(o, true);
    o += 2;
    const y = dv.getUint16(o, true);
    o += 2;
    const f = dv.getUint8(o++);
    const ownerIndex = dv.getUint8(o++);
    const ownerId = roster.get(ownerIndex)?.id ?? "";
    bullets.push({
      id,
      x,
      y,
      ownerId,
      homing: (f & BF_HOMING) !== 0,
      explosive: (f & BF_EXPLOSIVE) !== 0,
      pierce: (f & BF_PIERCE) !== 0,
    });
  }

  const powerups = [];
  const powerupCount = dv.getUint8(o++);
  for (let i = 0; i < powerupCount; i++) {
    const type = PUP_CODES[dv.getUint8(o++)] ?? "speed";
    const x = dv.getUint16(o, true);
    o += 2;
    const y = dv.getUint16(o, true);
    o += 2;
    powerups.push({ id: i, type, x, y });
  }

  const flags = [];
  const flagCount = dv.getUint8(o++);
  for (let i = 0; i < flagCount; i++) {
    const team = dv.getUint8(o++);
    const state = FLAG_STATES[dv.getUint8(o++)] ?? "home";
    const x = dv.getUint16(o, true);
    o += 2;
    const y = dv.getUint16(o, true);
    o += 2;
    const carrier = dv.getUint8(o++);
    flags.push({ team, state, x, y, carrier });
  }

  const blasts = [];
  const blastCount = dv.getUint8(o++);
  for (let i = 0; i < blastCount; i++) {
    const x = dv.getUint16(o, true);
    o += 2;
    const y = dv.getUint16(o, true);
    o += 2;
    blasts.push({ x, y });
  }

  const beams = [];
  const beamCount = dv.getUint8(o++);
  for (let i = 0; i < beamCount; i++) {
    const x1 = dv.getUint16(o, true);
    o += 2;
    const y1 = dv.getUint16(o, true);
    o += 2;
    const x2 = dv.getUint16(o, true);
    o += 2;
    const y2 = dv.getUint16(o, true);
    o += 2;
    beams.push({ x1, y1, x2, y2 });
  }

  const events = [];
  const eventCount = dv.getUint8(o++);
  for (let i = 0; i < eventCount; i++) {
    const type = dv.getUint8(o++);
    const killer = dv.getUint8(o++);
    const victim = dv.getUint8(o++);
    const points = dv.getInt16(o, true);
    o += 2;
    const streak = dv.getUint8(o++);
    const mult = dv.getUint8(o++);
    events.push({ type, killer, victim, points, streak, mult });
  }

  const wallHp: Array<{ index: number; hp: number }> = [];
  const wallHpCount = dv.getUint16(o, true);
  o += 2;
  for (let i = 0; i < wallHpCount; i++) {
    const index = dv.getUint16(o, true);
    o += 2;
    const hp = dv.getUint8(o++);
    wallHp.push({ index, hp });
  }

  return { t: 0, tanks, bullets, powerups, flags, blasts, beams, events, wallHp };
}

function decodeSlimSnapshot(
  dv: DataView,
  roster: Map<number, RosterEntry>,
  previous: SnapshotDTO | null
): SnapshotDTO {
  let o = 1;
  const previousTanks = new Map(previous?.tanks.map((t) => [t.index, t]) ?? []);

  const tanks: TankDTO[] = [];
  const tankCount = dv.getUint8(o++);
  for (let i = 0; i < tankCount; i++) {
    const index = dv.getUint8(o++);
    const x = dv.getUint16(o, true);
    o += 2;
    const y = dv.getUint16(o, true);
    o += 2;
    const bodyAngle = decAngle(dv.getInt16(o, true));
    o += 2;
    const turretAngle = decAngle(dv.getInt16(o, true));
    o += 2;
    const flags = dv.getUint8(o++);
    const respawnIn = decDs(dv.getUint8(o++));
    const r = roster.get(index);
    const prev = previousTanks.get(index);
    tanks.push({
      index,
      id: r?.id ?? prev?.id ?? String(index),
      name: r?.name ?? prev?.name ?? "?",
      color: r?.color ?? prev?.color ?? "#888888",
      team: r?.team ?? prev?.team ?? 0,
      maxHp: r?.maxHp ?? prev?.maxHp ?? 1,
      maxAmmo: r?.maxAmmo ?? prev?.maxAmmo ?? 0,
      x,
      y,
      bodyAngle,
      turretAngle,
      alive: (flags & 1) !== 0,
      boosted: (flags & 2) !== 0,
      shielded: (flags & 4) !== 0,
      charging: (flags & 8) !== 0,
      scoped: (flags & 16) !== 0,
      hp: prev?.hp ?? r?.maxHp ?? 1,
      ammo: prev?.ammo ?? r?.maxAmmo ?? 0,
      score: prev?.score ?? 0,
      respawnIn,
      reloadIn: prev?.reloadIn ?? 0,
      weaponCharges: prev ? { ...prev.weaponCharges } : {},
      livesLeft: prev?.livesLeft ?? 0,
      captures: prev?.captures ?? 0,
    });
  }

  return decodeSnapshotTail(dv, o, roster, tanks, previous);
}

function decodeSnapshotTail(
  dv: DataView,
  offset: number,
  roster: Map<number, RosterEntry>,
  tanks: TankDTO[],
  previous: SnapshotDTO | null
): SnapshotDTO {
  let o = offset;
  const bullets = [];
  const bulletCount = dv.getUint8(o++);
  for (let i = 0; i < bulletCount; i++) {
    const id = dv.getUint16(o, true);
    o += 2;
    const x = dv.getUint16(o, true);
    o += 2;
    const y = dv.getUint16(o, true);
    o += 2;
    const f = dv.getUint8(o++);
    const ownerIndex = dv.getUint8(o++);
    const ownerId = roster.get(ownerIndex)?.id ?? "";
    bullets.push({
      id,
      x,
      y,
      ownerId,
      homing: (f & BF_HOMING) !== 0,
      explosive: (f & BF_EXPLOSIVE) !== 0,
      pierce: (f & BF_PIERCE) !== 0,
    });
  }

  const powerups = [];
  const powerupCount = dv.getUint8(o++);
  for (let i = 0; i < powerupCount; i++) {
    const type = PUP_CODES[dv.getUint8(o++)] ?? "speed";
    const x = dv.getUint16(o, true);
    o += 2;
    const y = dv.getUint16(o, true);
    o += 2;
    powerups.push({ id: i, type, x, y });
  }

  const flags = [];
  const flagCount = dv.getUint8(o++);
  for (let i = 0; i < flagCount; i++) {
    const team = dv.getUint8(o++);
    const state = FLAG_STATES[dv.getUint8(o++)] ?? "home";
    const x = dv.getUint16(o, true);
    o += 2;
    const y = dv.getUint16(o, true);
    o += 2;
    const carrier = dv.getUint8(o++);
    flags.push({ team, state, x, y, carrier });
  }

  const blasts = [];
  const blastCount = dv.getUint8(o++);
  for (let i = 0; i < blastCount; i++) {
    const x = dv.getUint16(o, true);
    o += 2;
    const y = dv.getUint16(o, true);
    o += 2;
    blasts.push({ x, y });
  }

  const beams = [];
  const beamCount = dv.getUint8(o++);
  for (let i = 0; i < beamCount; i++) {
    const x1 = dv.getUint16(o, true);
    o += 2;
    const y1 = dv.getUint16(o, true);
    o += 2;
    const x2 = dv.getUint16(o, true);
    o += 2;
    const y2 = dv.getUint16(o, true);
    o += 2;
    beams.push({ x1, y1, x2, y2 });
  }

  const events = [];
  const eventCount = dv.getUint8(o++);
  for (let i = 0; i < eventCount; i++) {
    const type = dv.getUint8(o++);
    const killer = dv.getUint8(o++);
    const victim = dv.getUint8(o++);
    const points = dv.getInt16(o, true);
    o += 2;
    const streak = dv.getUint8(o++);
    const mult = dv.getUint8(o++);
    events.push({ type, killer, victim, points, streak, mult });
  }

  // Slim snapshots don't carry wall HP; inherit it from the last full snapshot.
  const wallHp = previous?.wallHp ?? [];
  return { t: 0, tanks, bullets, powerups, flags, blasts, beams, events, wallHp };
}
